// Campaign Cache Management Utility
// Centralized caching system for campaign data to improve navigation performance

import { normalizeCampaignStatus } from '../data/campaignStatus';

class CampaignCache {
  constructor() {
    this.cache = new Map();
    this.TTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.listeners = new Set();
  }

  // Set cache with TTL
  set(key, data, customTTL = null) {
    const ttl = customTTL || this.TTL;
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl
    };
    
    this.cache.set(key, cacheEntry);
    this.notifyListeners('set', key, data);
    
    // Auto-cleanup expired entries
    setTimeout(() => {
      this.delete(key);
    }, ttl);
  }

  // Get cache entry if not expired
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.notifyListeners('expired', key);
      return null;
    }

    this.notifyListeners('hit', key, entry.data);
    return entry.data;
  }

  // Check if key exists and is valid
  has(key) {
    return this.get(key) !== null;
  }

  // Delete specific cache entry
  delete(key) {
    const existed = this.cache.has(key);
    this.cache.delete(key);
    if (existed) {
      this.notifyListeners('delete', key);
    }
    return existed;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.notifyListeners('clear');
  }

  // Get cache stats
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    const valid = entries.filter(([_, entry]) => now - entry.timestamp <= entry.ttl);
    const expired = entries.length - valid.length;

    return {
      total: entries.length,
      valid: valid.length,
      expired,
      keys: valid.map(([key]) => key)
    };
  }

  // Add cache event listener
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify listeners of cache events
  notifyListeners(event, key, data = null) {
    this.listeners.forEach(listener => {
      try {
        listener({ event, key, data, timestamp: Date.now() });
      } catch (error) {
        console.error('Cache listener error:', error);
      }
    });
  }

  // Invalidate cache entries by pattern
  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.delete(key));
    return keysToDelete.length;
  }

  // Batch operations
  setMultiple(entries) {
    entries.forEach(({ key, data, ttl }) => {
      this.set(key, data, ttl);
    });
  }

  getMultiple(keys) {
    return keys.reduce((result, key) => {
      const data = this.get(key);
      if (data !== null) {
        result[key] = data;
      }
      return result;
    }, {});
  }
}

// Create singleton instance
const campaignCache = new CampaignCache();

// Cache key generators
export const generateCacheKey = {
  campaign: (id) => `campaign_${id}`,
  userCampaigns: (companyId) => `user_campaigns_${companyId}`,
  campaignStats: (campaignId) => `stats_${campaignId}`,
  campaignEvents: (campaignId, timeframe) => `events_${campaignId}_${timeframe}`,
  chartData: (campaignIds, view) => `chart_${campaignIds.join('_')}_${view}`
};

// Helper functions for common cache operations
export const cacheHelpers = {
  // Cache campaign data with appropriate TTL based on status
  cacheCampaign: (campaign) => {
    const status = normalizeCampaignStatus(campaign?.status);
    const ttl = status === 'completed'
      ? 24 * 60 * 60 * 1000  // 24 hours for completed campaigns
      : 5 * 60 * 1000;       // 5 minutes for draft/published/paused
    campaignCache.set(generateCacheKey.campaign(campaign.id), campaign, ttl);
  },

  // Cache user's all campaigns
  cacheUserCampaigns: (companyId, campaigns) => {
    campaignCache.set(generateCacheKey.userCampaigns(companyId), campaigns);
    
    // Also cache individual campaigns
    campaigns.forEach(campaign => {
      cacheHelpers.cacheCampaign(campaign);
    });
  },

  // Get cached campaign
  getCachedCampaign: (campaignId) => {
    return campaignCache.get(generateCacheKey.campaign(campaignId));
  },

  // Get cached user campaigns
  getCachedUserCampaigns: (companyId) => {
    return campaignCache.get(generateCacheKey.userCampaigns(companyId));
  },

  /** Invalidate cached list of campaigns for a company (e.g. after creating a new game) */
  invalidateUserCampaigns: (companyId) => {
    if (companyId) campaignCache.delete(generateCacheKey.userCampaigns(companyId));
  },

  // Invalidate campaign-related cache when campaign is updated
  invalidateCampaign: (campaignId) => {
    campaignCache.delete(generateCacheKey.campaign(campaignId));
    campaignCache.invalidatePattern(`stats_${campaignId}`);
    campaignCache.invalidatePattern(`events_${campaignId}`);
    campaignCache.invalidatePattern(`chart_.*${campaignId}`);
  },

  /**
   * Clear analytics-related localStorage so dashboard/campaign stats charts
   * refetch after status change (e.g. go live). Keys: dashboard_chart_cache_v6_*,
   * game_stats_cache_v1_* that contain this campaign id.
   */
  invalidateAnalyticsLocalStorage: (campaignId) => {
    if (typeof localStorage === 'undefined') return;
    const prefixes = ['dashboard_chart_cache_v6_', 'game_stats_cache_v1_'];
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const hasPrefix = prefixes.some((p) => key.startsWith(p));
      const containsCampaign = key.includes(campaignId);
      if (hasPrefix && containsCampaign) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    if (keysToRemove.length) {
      console.log(`🔄 Cleared ${keysToRemove.length} analytics localStorage key(s) for campaign ${campaignId}`);
    }
  },

  // Cache campaign stats with dynamic TTL based on activity level
  cacheStats: (campaignId, stats, isComplete = false, totalPlays = 0) => {
    let ttl;
    
    if (isComplete) {
      ttl = 24 * 60 * 60 * 1000; // 24 hours for completed campaigns
    } else {
      // Dynamic cache based on activity level for instant results
      if (totalPlays === 0) ttl = 10 * 1000; // 10 seconds for empty campaigns
      else if (totalPlays < 10) ttl = 15 * 1000; // 30 seconds for very low volume
      else if (totalPlays < 50) ttl = 60 * 1000; // 1 minute for low volume
      else if (totalPlays < 200) ttl = 2 * 60 * 1000; // 2 minutes for medium volume
      else ttl = 5 * 60 * 1000; // 5 minutes for high volume
    }
    
    campaignCache.set(generateCacheKey.campaignStats(campaignId), stats, ttl);
  },

  // Get cache statistics for debugging
  getDebugInfo: () => {
    const stats = campaignCache.getStats();
    return {
      ...stats,
      memoryUsage: JSON.stringify([...campaignCache.cache.entries()]).length,
      cacheEntries: stats.keys.map(key => ({
        key,
        size: JSON.stringify(campaignCache.get(key)).length
      }))
    };
  }
};

// Development mode cache monitoring
if (process.env.NODE_ENV === 'development') {
  campaignCache.addListener((_event) => {

  });
}

export default campaignCache;
export { campaignCache }; 